/* eslint-disable react/no-unstable-nested-components */
import { Button, DataTable, FlexLayout, Icon } from '@deity-io/portal-ui';
import {
  BaseHelmet,
  ConfirmationModal,
  CreateEditEndpointModal,
  EndpointMethodBadge,
  EndpointTypeBadge
} from 'components';
import { APOLLO_BASE_OPTIONS, ENTITY_PATH, ENTITY_PATH_ID, toLocalDateString } from 'contracts/constants';
import { DEFAULT_PAGINATION_SIZE } from 'contracts/constants/page-title';
import { EntityWrapper } from 'layouts';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import {
  GetEndpointsByPackageVersionDocument,
  useDeleteEndpointMutation,
  useGetEndpointsByPackageVersionLazyQuery
} from 'services/graphql/generated/hooks';
import { getParams } from 'utils';
import { v4 } from 'uuid';

interface EndpointsProps {
  isEditable: boolean;
}
const Endpoints: FC<EndpointsProps> = ({ isEditable }) => {
  const history = useHistory();
  const [paging, setPaging] = useState({ page: 1, pageSize: DEFAULT_PAGINATION_SIZE });
  const { url } = useRouteMatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [endpointToDelete, setEndpointToDelete] = useState<any>();
  const { pathname } = useLocation();
  const [getAllEndpoints, { loading, data: endpoints, fetchMore }] =
    useGetEndpointsByPackageVersionLazyQuery(APOLLO_BASE_OPTIONS);
  const [newEndpointKey, setNewEndpointKey] = useState(v4());
  const params: any = getParams(pathname, [ENTITY_PATH_ID, ENTITY_PATH]);
  const totalItems = endpoints?.getEndpointsByPackageVersion?.totalItems;
  const data = endpoints?.getEndpointsByPackageVersion?.data;

  const [deleteEndpoint] = useDeleteEndpointMutation({
    refetchQueries: [
      {
        query: GetEndpointsByPackageVersionDocument,
        variables: {
          packageVersionUuid: params?.versionUuid,
          offset: 0,
          limit: endpoints?.getEndpointsByPackageVersion?.data.length || paging.pageSize
        }
      }
    ]
  });

  useEffect(() => {
    if (params?.versionUuid) {
      getAllEndpoints({
        variables: {
          packageVersionUuid: params.versionUuid,
          offset: (paging.page - 1) * paging.pageSize,
          limit: paging.pageSize
        }
      });
    }
  }, [pathname, paging]);

  const NewEndpointButton = () => (
    <Button
      variant="outlined"
      disabled={isModalOpen}
      onClick={() => setIsModalOpen(true)}
      startIcon={<Icon icon="add" />}
    >
      New Endpoint
    </Button>
  );

  return (
    <>
      <BaseHelmet page="Endpoints" />

      <EntityWrapper title="Endpoints" button={<NewEndpointButton />}>
        <DataTable
          data={data || []}
          isLoading={loading}
          noResultsMsg="No endpoints available for this version"
          columns={[
            {
              key: 'WebService',
              name: 'Service',
              formatter: ({ value }) => <b>{value.name}</b>
            },
            {
              key: 'name',
              name: 'ENDPOINT',
              formatter: ({ value }) => <b>{value}</b>,
              width: 40
            },
            {
              key: 'WebService',
              name: 'TYPE',
              align: 'left',
              formatter: props => <EndpointTypeBadge schema={{ type: props.value.schema?.type }} />
            },
            {
              key: 'schema',
              name: 'METHOD',
              align: 'left',
              formatter: props => <EndpointMethodBadge schema={props.value} />
            },
            {
              key: 'modifiedAt',
              name: 'LAST MODIFIED',
              formatter: ({ value }) => toLocalDateString(value!)
            },
            {
              key: 'modifiedBy',
              name: 'LAST MODIFIED BY',
              formatter: () => <div>John Smith</div>
            },
            {
              key: 'uuid',
              name: 'ACTIONS',
              align: 'left',
              isActionCell: true,
              formatter: ({ value }) => (
                <FlexLayout>
                  <Button endIcon={<Icon icon="visibility" />} onClick={() => history.push(`${url}/${value}`)} />

                  {isEditable && (
                    <Button
                      endIcon={<Icon icon="delete" color="error" />}
                      onClick={() => {
                        setEndpointToDelete(value);
                        setIsDeleteModalOpen(true);
                      }}
                    />
                  )}
                </FlexLayout>
              )
            }
          ]}
          pageSize={paging.pageSize}
          onPageSizeChange={size => {
            setPaging({ ...paging, pageSize: size });
          }}
          totalResults={totalItems || 0}
          page={paging.page}
          showPagination
          onPageChange={pageNr => {
            setPaging({ ...paging, page: pageNr });
          }}
          onRowClick={endpoint => history.push(`${url}/${endpoint.uuid}`)}
          fullClickable
        />
      </EntityWrapper>

      <CreateEditEndpointModal
        key={newEndpointKey}
        isOpen={isModalOpen}
        versionUuid={params?.versionUuid}
        setOpenState={state => setIsModalOpen(state)}
        onPackageCreated={() => {
          setIsModalOpen(false);
          setNewEndpointKey(v4());
          setPaging({ ...paging, page: 1 });
        }}
      />
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        entityType="endpoint"
        entityUuid={endpointToDelete}
        onConfirm={uuid =>
          deleteEndpoint({
            variables: { packageVersionUuid: params.versionUuid, uuid: uuid! }
          })
        }
      />
    </>
  );
};

export default Endpoints;
